import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Bot,
  Check,
  ChevronRight,
  ClipboardCheck,
  FileDiff,
  GitBranch,
  Layers3,
  Lock,
  Network,
  Play,
  ShieldCheck,
  Sparkles,
  TimerReset,
  UsersRound
} from "lucide-react";
import { useMemo, useState } from "react";
import cegLogo from "./assets/ceg-logo.png";
import { fixture } from "./fixture";

type Path = "mature" | "fragmented" | null;
type Stage =
  | "select"
  | "baseline"
  | "change"
  | "staging"
  | "impact"
  | "documents"
  | "readiness"
  | "executive";
type Applicability = "Applicable" | "Not Applicable" | "Needs Investigation" | null;

const stageLabels: Record<Stage, string> = {
  select: "Client",
  baseline: "Knowledge",
  change: "Detection",
  staging: "Staging",
  impact: "Impact",
  documents: "Updates",
  readiness: "Readiness",
  executive: "Executive"
};

const agentCards = [
  ["Knowledge Steward", "Maintains graph integrity and source authority", Layers3],
  ["Change Detection Agent", "Monitors requirement changes for staging", Sparkles],
  ["Impact Analysis Agent", "Proposes affected work, controls, and systems", Network],
  ["People Impact Agent", "Maps changes to roles without PII", UsersRound],
  ["Change Readiness Agent", "Checks activation conditions before launch", ShieldCheck]
] as const;

export function App() {
  const [path, setPath] = useState<Path>(null);
  const [stage, setStage] = useState<Stage>("select");
  const [baselineConfirmed, setBaselineConfirmed] = useState(false);
  const [selectedNode, setSelectedNode] = useState<string>(fixture.graph.nodes[0].id);
  const [applicability, setApplicability] = useState<Applicability>(null);
  const [impactDecisions, setImpactDecisions] = useState<Record<string, string>>({});
  const [draftsAccepted, setDraftsAccepted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [attemptedActivation, setAttemptedActivation] = useState(false);
  const [activated, setActivated] = useState(false);

  const currentNode = useMemo(
    () => fixture.graph.nodes.find((node) => node.id === selectedNode) ?? fixture.graph.nodes[0],
    [selectedNode]
  );

  const selectedLinks = fixture.graph.links.filter((link) => link[0] === selectedNode || link[1] === selectedNode);
  const readiness = completed ? fixture.readiness.ready : fixture.readiness.atRisk;
  const allImpactsDecided = fixture.impacts.every((impact) => impactDecisions[impact[0]]);

  function choosePath(nextPath: Exclude<Path, null>) {
    setPath(nextPath);
    setStage(nextPath === "mature" ? "baseline" : "baseline");
  }

  function resetDemo() {
    setPath(null);
    setStage("select");
    setBaselineConfirmed(false);
    setApplicability(null);
    setImpactDecisions({});
    setDraftsAccepted(false);
    setCompleted(false);
    setAttemptedActivation(false);
    setActivated(false);
  }

  return (
    <main>
      <header className="topbar">
        <div className="brand-lockup">
          <img src={cegLogo} alt="Creative Excellence Group" />
          <div>
            <h1>
              CERTEVON Intelligence™
              <span className="brand-attribution">software by Creative Excellence Group</span>
            </h1>
          </div>
        </div>
        <button className="ghost" onClick={resetDemo}>
          Reset Prototype
        </button>
      </header>

      <nav className="progress" aria-label="Prototype progress">
        {(Object.keys(stageLabels) as Stage[]).map((key) => (
          <button key={key} className={stage === key ? "active" : ""} onClick={() => path && setStage(key)} disabled={!path && key !== "select"}>
            <span>{stageLabels[key]}</span>
          </button>
        ))}
      </nav>

      {stage === "select" && <ClientSelection onChoose={choosePath} />}
      {stage === "baseline" && path && (
        <Baseline
          path={path}
          confirmed={baselineConfirmed}
          onConfirm={() => setBaselineConfirmed(true)}
          selectedNode={selectedNode}
          setSelectedNode={setSelectedNode}
          currentNode={currentNode}
          selectedLinks={selectedLinks}
          onNext={() => setStage("change")}
        />
      )}
      {stage === "change" && <ChangeDetection onStage={() => setStage("staging")} />}
      {stage === "staging" && (
        <Staging
          applicability={applicability}
          setApplicability={setApplicability}
          onNext={() => setStage("impact")}
        />
      )}
      {stage === "impact" && (
        <ImpactAnalysis
          decisions={impactDecisions}
          setDecision={(name, decision) => setImpactDecisions((prev) => ({ ...prev, [name]: decision }))}
          allDecided={allImpactsDecided}
          onNext={() => setStage("documents")}
        />
      )}
      {stage === "documents" && (
        <Documents accepted={draftsAccepted} onAccept={() => setDraftsAccepted(true)} onNext={() => setStage("readiness")} />
      )}
      {stage === "readiness" && (
        <Readiness
          readiness={readiness}
          completed={completed}
          attemptedActivation={attemptedActivation}
          activated={activated}
          onAttempt={() => setAttemptedActivation(true)}
          onComplete={() => setCompleted(true)}
          onAuthorize={() => setActivated(true)}
          onExecutive={() => setStage("executive")}
        />
      )}
      {stage === "executive" && <Executive activated={activated} />}
    </main>
  );
}

function ClientSelection({ onChoose }: { onChoose: (path: "mature" | "fragmented") => void }) {
  return (
    <section className="screen landing">
      <div className="landing-hero">
        <div className="landing-copy">
          <p className="eyebrow">Operational Knowledge & Change Intelligence</p>
          <h2>Most teams know a requirement changed. They do not know everything that change touches.</h2>
          <p>
            CERTEVON Intelligence™ connects requirements to the real operating system of the business: processes, procedures, controls, forms,
            systems, roles, training, and job aids. When something important changes, leaders can see what is affected and what must
            be ready before the change goes live.
          </p>
          <div className="landing-actions">
            <button className="primary" onClick={() => onChoose("mature")}>
              Simulate Mature Environment
              <ArrowRight size={16} />
            </button>
            <button className="secondary" onClick={() => onChoose("fragmented")}>
              Simulate Fragmented Environment
            </button>
          </div>
        </div>

        <div className="landing-visual">
          <div className="doc-showcase" aria-label="Controlled documentation and operational knowledge">
            <img
              src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1200&q=90"
              alt="Business documentation and controlled records on a desk"
            />
          </div>
          <div className="change-card">
            <span>New requirement</span>
            <strong>48 hours becomes 24 hours + containment plan</strong>
          </div>
          <div className="signal-grid">
            {[
              ["Processes", "1 affected"],
              ["Roles", "4 affected"],
              ["Training", "2 assets"],
              ["Systems", "1 dependency"]
            ].map(([label, value]) => (
              <div key={label}>
                <span>{label}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="problem-band">
        {[
          ["The Problem", "Requirements, procedures, systems, and training drift apart. People discover the gaps late, usually during implementation, audit, or customer pressure."],
          ["The Platform", "CERTEVON Intelligence™ keeps an operational knowledge graph that shows how work really fits together across documents, systems, controls, and roles."],
          ["The Outcome", "Before activation, leaders know what changed, who is affected, what evidence is missing, and whether the organization is ready."]
        ].map(([title, body]) => (
          <article key={title}>
            <h3>{title}</h3>
            <p>{body}</p>
          </article>
        ))}
      </div>

      <div className="workflow-visual">
        {[
          [TimerReset, "Requirement Changes", "A customer, regulator, or internal standard changes a rule."],
          [Network, "Impact Is Mapped", "CERTEVON Intelligence™ traces the change through processes, documents, systems, controls, and roles."],
          [UsersRound, "People Are Prepared", "Training, job aids, forms, and manager communications are staged for the affected roles."],
          [ShieldCheck, "Activation Is Controlled", "The future state stays staged until readiness evidence is complete and authorized."]
        ].map(([Icon, title, body]) => (
          <article key={title as string}>
            {typeof Icon !== "string" && <Icon size={22} />}
            <h3>{title as string}</h3>
            <p>{body as string}</p>
          </article>
        ))}
      </div>

      <div className="demo-section">
        <div>
          <p className="eyebrow">Choose a Starting Point</p>
          <h2>Run the same CERTEVON Intelligence™ workflow from either customer reality.</h2>
        </div>
        <div className="choice-panel">
          <EnvironmentCard kind="mature" onChoose={() => onChoose("mature")} />
          <EnvironmentCard kind="fragmented" onChoose={() => onChoose("fragmented")} />
        </div>
      </div>

      <div className="agent-strip landing-agents">
        {agentCards.map(([name, caption, Icon]) => (
          <div className="agent" key={name}>
            <Icon size={18} />
            <strong>{name}</strong>
            <span>{caption}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function EnvironmentCard({ kind, onChoose }: { kind: "mature" | "fragmented"; onChoose: () => void }) {
  const env = fixture.environments[kind];
  const statRows = kind === "mature" ? fixture.environments.mature.stats : fixture.environments.fragmented.discovery;
  return (
    <article className="environment">
      <div className="card-head">
        <span className="path-label">{kind === "mature" ? "Path A" : "Path B"}</span>
        <h3>{env.client}</h3>
        <p>{env.title}</p>
      </div>
      <div className="metric-grid">
        {statRows.map(([label, value]) => (
          <div key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>
      {kind === "mature" ? (
        <p className="notice">{fixture.environments.mature.statement}</p>
      ) : (
        <div className="issue-cloud">
          {fixture.environments.fragmented.issues.map((issue) => (
            <span key={issue}>{issue}</span>
          ))}
        </div>
      )}
      <button className="primary" onClick={onChoose}>
        {kind === "mature" ? "Simulate Mature Environment" : "Review Discovery Findings"}
        <ArrowRight size={16} />
      </button>
    </article>
  );
}

function Baseline(props: {
  path: Exclude<Path, null>;
  confirmed: boolean;
  onConfirm: () => void;
  selectedNode: string;
  setSelectedNode: (id: string) => void;
  currentNode: (typeof fixture.graph.nodes)[number];
  selectedLinks: readonly (readonly [string, string, string])[];
  onNext: () => void;
}) {
  return (
    <section className="screen">
      <SectionTitle icon={<Network />} label="Trusted Operational Knowledge" title="Both starting environments converge into one governed baseline." />
      {props.path === "fragmented" && (
        <div className="review-band">
          <h3>Human-reviewed baseline recommendations</h3>
          <div className="recommendations">
            {fixture.environments.fragmented.recommendations.map(([rec, status]) => (
              <div key={rec}>
                <strong>{rec}</strong>
                <span>{status}</span>
              </div>
            ))}
          </div>
          <button className={props.confirmed ? "success" : "primary"} onClick={props.onConfirm}>
            <Check size={16} />
            {props.confirmed ? "Trusted Baseline Established" : "Confirm Selected Recommendations"}
          </button>
        </div>
      )}

      <div className="graph-layout">
        <div className="graph" aria-label="Operational knowledge graph">
          <svg viewBox="0 0 100 100" role="img">
            {fixture.graph.links.map(([from, to]) => {
              const a = fixture.graph.nodes.find((node) => node.id === from)!;
              const b = fixture.graph.nodes.find((node) => node.id === to)!;
              return <line key={`${from}-${to}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />;
            })}
          </svg>
          {fixture.graph.nodes.map((node) => (
            <button
              key={node.id}
              className={`node ${node.type.toLowerCase().replaceAll(" ", "-")} ${props.selectedNode === node.id ? "selected" : ""}`}
              style={{ left: `${node.x}%`, top: `${node.y}%` }}
              onClick={() => props.setSelectedNode(node.id)}
            >
              <span>{node.type}</span>
              <strong>{node.id}</strong>
            </button>
          ))}
        </div>
        <aside className="detail-panel">
          <p className="eyebrow">{props.currentNode.type}</p>
          <h3>{props.currentNode.name}</h3>
          <InfoRows
            rows={[
              ["Owner", props.currentNode.owner],
              ["Active version", props.currentNode.version],
              ["Source", props.currentNode.source]
            ]}
          />
          <h4>Relationships</h4>
          {props.selectedLinks.map(([from, to, why]) => (
            <p className="relationship" key={`${from}-${to}`}>
              <strong>{from === props.currentNode.id ? to : from}</strong>
              <span>{why}</span>
            </p>
          ))}
          <button className="primary wide" onClick={props.onNext} disabled={props.path === "fragmented" && !props.confirmed}>
            Continue to Requirement Change
            <ChevronRight size={16} />
          </button>
        </aside>
      </div>
    </section>
  );
}

function ChangeDetection({ onStage }: { onStage: () => void }) {
  return (
    <section className="screen two-col">
      <div>
        <SectionTitle icon={<TimerReset />} label="New Requirement Change Detected" title="Northstar Motors changed a time-critical quality obligation." />
        <div className="requirement-card">
          <p className="eyebrow">Current Requirement</p>
          <h3>{fixture.change.current}</h3>
        </div>
        <div className="requirement-card future">
          <p className="eyebrow">New Requirement</p>
          <h3>{fixture.change.future}</h3>
          <InfoRows rows={[["Published", fixture.change.published], ["Effective", fixture.change.effective], ["Customer", fixture.change.customer]]} />
        </div>
        <button className="primary" onClick={onStage}>
          <Play size={16} />
          Stage Change
        </button>
      </div>
      <AgentRail active="Change Detection Agent" />
    </section>
  );
}

function Staging({
  applicability,
  setApplicability,
  onNext
}: {
  applicability: Applicability;
  setApplicability: (value: Applicability) => void;
  onNext: () => void;
}) {
  return (
    <section className="screen">
      <SectionTitle icon={<GitBranch />} label="Change Staging Environment" title="Current active state and proposed future state are kept visibly separate." />
      <div className="state-table">
        <div className="state-head current">Current Active State</div>
        <div className="state-head future">Proposed Future State: Staged, Not Active</div>
        {fixture.staged.map(([item, current, future, status]) => (
          <div className="state-row" key={item}>
            <div>
              <span>{item}</span>
              <strong>{current}</strong>
            </div>
            <div>
              <span>{status}</span>
              <strong>{future}</strong>
            </div>
          </div>
        ))}
      </div>
      <div className="applicability">
        <h3>Applicability Review</h3>
        <p>A simulated human decision is required before CERTEVON Intelligence™ proceeds to impact analysis.</p>
        <div className="segmented">
          {(["Applicable", "Not Applicable", "Needs Investigation"] as const).map((option) => (
            <button key={option} className={applicability === option ? "selected" : ""} onClick={() => setApplicability(option)}>
              {option}
            </button>
          ))}
        </div>
        <button className="primary" onClick={onNext} disabled={applicability !== "Applicable"}>
          Run Impact Analysis
          <ArrowRight size={16} />
        </button>
      </div>
    </section>
  );
}

function ImpactAnalysis(props: {
  decisions: Record<string, string>;
  setDecision: (name: string, decision: string) => void;
  allDecided: boolean;
  onNext: () => void;
}) {
  return (
    <section className="screen">
      <SectionTitle icon={<Activity />} label="Operational and People Impact" title="AI proposes impacts. Humans validate consequential decisions." />
      <AgentRail active="Impact Analysis Agent" compact />
      <div className="impact-grid">
        {fixture.impacts.map(([name, why, evidence, level, confidence, owner]) => (
          <article className="impact-card" key={name}>
            <div className="impact-top">
              <h3>{name}</h3>
              <span className={`level ${level.toLowerCase()}`}>{level}</span>
            </div>
            <InfoRows rows={[["Why affected", why], ["Supporting evidence", evidence], ["Confidence", confidence], ["Owner", owner]]} />
            <div className="small-actions">
              {["Confirm Impact", "Not Impacted", "Needs Investigation"].map((decision) => (
                <button key={decision} className={props.decisions[name] === decision ? "selected" : ""} onClick={() => props.setDecision(name, decision)}>
                  {decision}
                </button>
              ))}
            </div>
          </article>
        ))}
      </div>
      <div className="people-table">
        <h3>People Impact Analysis</h3>
        <DataTable
          headers={["Role", "Current way", "Proposed future way", "Impact", "Population", "Why affected"]}
          rows={fixture.people}
        />
      </div>
      <div className="enablement">
        {fixture.enablement.map((item) => (
          <span key={item}>{item}</span>
        ))}
      </div>
      <button className="primary" onClick={props.onNext} disabled={!props.allDecided}>
        Generate Proposed Updates
        <FileDiff size={16} />
      </button>
    </section>
  );
}

function Documents({ accepted, onAccept, onNext }: { accepted: boolean; onAccept: () => void; onNext: () => void }) {
  return (
    <section className="screen">
      <SectionTitle icon={<FileDiff />} label="Document Engineering" title="Substantive approved content is staged for review, never silently changed." />
      <div className="draft-grid">
        {fixture.drafts.map(([doc, redline, draft]) => (
          <article className="draft" key={doc}>
            <h3>{doc}</h3>
            <div className="tabs">
              <button>View Redline</button>
              <button>View Standardized Draft</button>
            </div>
            <p><strong>Redline:</strong> {redline}</p>
            <p><strong>Standardized draft:</strong> {draft}</p>
          </article>
        ))}
      </div>
      <button className={accepted ? "success" : "primary"} onClick={onAccept}>
        <Check size={16} />
        {accepted ? "Accepted for Staging" : "Accept for Staging"}
      </button>
      <div className="people-table">
        <h3>Implementation Plan</h3>
        <DataTable headers={["Owner", "Required action", "Due date", "Evidence required", "Status"]} rows={fixture.actions} />
      </div>
      <button className="primary" onClick={onNext} disabled={!accepted}>
        Advance Toward January 1
        <ArrowRight size={16} />
      </button>
    </section>
  );
}

function Readiness(props: {
  readiness: readonly (readonly [string, number, string])[];
  completed: boolean;
  attemptedActivation: boolean;
  activated: boolean;
  onAttempt: () => void;
  onComplete: () => void;
  onAuthorize: () => void;
  onExecutive: () => void;
}) {
  return (
    <section className="screen two-col">
      <div>
        <SectionTitle icon={<ClipboardCheck />} label="Activation Control" title="Activation remains blocked until readiness evidence is complete." />
        <div className="readiness-grid">
          {props.readiness.map(([name, value, note]) => (
            <div className="readiness" key={name}>
              <div>
                <span>{name}</span>
                <strong>{value}%</strong>
              </div>
              <meter value={value} min={0} max={100} />
              <p>{note}</p>
            </div>
          ))}
        </div>
        {!props.completed && (
          <button className="primary" onClick={props.onAttempt}>
            Attempt Activation
            <Lock size={16} />
          </button>
        )}
        {props.attemptedActivation && !props.completed && (
          <div className="blocked">
            <AlertTriangle />
            <div>
              <h3>Activation Blocked</h3>
              <p>CRM configuration not verified. Required Quality Engineer training incomplete.</p>
              <button className="primary" onClick={props.onComplete}>Simulate Completion</button>
            </div>
          </div>
        )}
        {props.completed && !props.activated && (
          <div className="authorization">
            <h3>Ready for Activation</h3>
            <p>All readiness categories are 100%. Authorization records the controlled transition from staged future state to active baseline.</p>
            <button className="primary" onClick={props.onAuthorize}>
              Authorize Activation
              <ShieldCheck size={16} />
            </button>
          </div>
        )}
        {props.activated && (
          <div className="activated">
            <Check />
            <div>
              <h3>Activated</h3>
              <p>New requirement, QP-17 v5, F-18 v3, and T-22 v4 are active. The prior baseline remains in history.</p>
              <button className="primary" onClick={props.onExecutive}>Open Executive View</button>
            </div>
          </div>
        )}
      </div>
      <AgentRail active="Change Readiness Agent" />
    </section>
  );
}

function Executive({ activated }: { activated: boolean }) {
  return (
    <section className="screen">
      <SectionTitle icon={<ShieldCheck />} label="Executive Intelligence" title="Knowledge health, change portfolio, organizational impact, and traceability in one view." />
      <div className="exec-grid">
        <ExecPanel title="Knowledge Health" rows={fixture.executive.health} />
        <ExecPanel title="Change Portfolio" rows={fixture.executive.portfolio} />
        <ExecPanel title="Organizational Impact" rows={fixture.executive.impact} />
        <article className="exec-panel">
          <h3>Upcoming Effective Dates</h3>
          {fixture.executive.dates.map((date) => (
            <p className="date-row" key={date}>{date}</p>
          ))}
        </article>
      </div>
      <article className="audit">
        <h3>Change History and Traceability</h3>
        <div className="timeline">
          {[
            "Source requirement captured from Northstar Motors",
            "Previous version: 48-hour reporting baseline",
            "New version: 24-hour reporting with containment plan",
            "Applicability decision: Applicable",
            "AI-proposed impacts reviewed and human decisions recorded",
            "Owners assigned to documents, system configuration, training, and job aids",
            "Implementation evidence collected",
            "Readiness reached 100% across operational, people, and enablement categories",
            activated ? "Authorized activation completed today" : "Awaiting authorized activation"
          ].map((item) => (
            <div key={item}><span />{item}</div>
          ))}
        </div>
      </article>
    </section>
  );
}

function SectionTitle({ icon, label, title }: { icon: React.ReactNode; label: string; title: string }) {
  return (
    <div className="section-title">
      <div>{icon}</div>
      <p className="eyebrow">{label}</p>
      <h2>{title}</h2>
    </div>
  );
}

function InfoRows({ rows }: { rows: readonly (readonly [string, string])[] }) {
  return (
    <div className="info-rows">
      {rows.map(([label, value]) => (
        <p key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
        </p>
      ))}
    </div>
  );
}

function DataTable({ headers, rows }: { headers: readonly string[]; rows: readonly (readonly string[])[] }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.join("-")}>{row.map((cell) => <td key={cell}>{cell}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AgentRail({ active, compact = false }: { active: string; compact?: boolean }) {
  return (
    <aside className={compact ? "agent-rail compact" : "agent-rail"}>
      <p className="eyebrow">Bounded AI Agents</p>
      {agentCards.map(([name, caption, Icon]) => (
        <div className={active === name ? "agent active" : "agent"} key={name}>
          <Icon size={18} />
          <strong>{name}</strong>
          <span>{caption}</span>
        </div>
      ))}
      <div className="guardrail">
        <Bot size={18} />
        <span>Agents propose. Authorized humans validate consequential changes.</span>
      </div>
    </aside>
  );
}

function ExecPanel({ title, rows }: { title: string; rows: readonly (readonly [string, string])[] }) {
  return (
    <article className="exec-panel">
      <h3>{title}</h3>
      {rows.map(([label, value]) => (
        <div className="exec-metric" key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </article>
  );
}
